import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Upload, Wand2, Loader2, ImageIcon, Trash2,
  History as HistoryIcon, Sparkles, ArrowLeft, Download, Layers, ToggleLeft, ToggleRight,
} from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WISH_SUGGESTIONS = {
  room: ['läshörna med golvlampa', 'större soffa i beige', 'växter i hörnen', 'takfläkt', 'bokhylla längs väggen'],
  garden: ['stenlagd gång', 'rabatt med lavendel', 'äppelträd', 'liten damm', 'gräsmatta med kanter'],
  patio: ['pergola med ljusslinga', 'utomhussoffa', 'markiser', 'grill-hörna', 'stora terrakotta-krukor'],
  pool_area: ['pool med romerska trappor', 'solstolar i teak', 'sail-skuggduk', 'palmer', 'poolhus'],
  greenhouse: ['glasväxthus i svart aluminium', 'orangeri i vitmålat trä', 'plåttak i koppar', 'liten spis', 'kruk-bänk'],
  facade: ['nya fönsterluckor', 'panelklädnad i svart', 'ny ytterdörr', 'entré-tak', 'utomhusbelysning'],
  balcony: ['hopfällbart bord & stolar', 'krukor med blommor', 'utomhusmatta', 'ljusslinga', 'sicksack-skuggduk'],
};

/** Compress image to keep base64 under ~1.5MB and max edge ~1600px */
async function fileToCompressedBase64(file, maxEdge = 1600, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function downloadB64Image(b64, filename) {
  const src = b64.startsWith('data:') ? b64 : `data:image/png;base64,${b64}`;
  const a = document.createElement('a');
  a.href = src;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function Redesign() {
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [spaceType, setSpaceType] = useState('garden');
  const [imageB64, setImageB64] = useState(null);
  const [wishes, setWishes] = useState('');
  const [styleNote, setStyleNote] = useState('');
  const [variations, setVariations] = useState(2);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {id, images_b64[], errors[]}
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detail, setDetail] = useState(null); // full history item with images
  const [showSlider, setShowSlider] = useState(true);
  const [combineIdxs, setCombineIdxs] = useState([]);
  const [combining, setCombining] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/redesign/space-types`, { withCredentials: true });
        setSpaceTypes(data.space_types || []);
      } catch (e) { /* noop */ }
      await refreshHistory();
    })();
  }, []);

  const refreshHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/redesign/history`, { withCredentials: true });
      setHistory(data.history || []);
    } catch (e) { /* noop */ }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpe?g|png|webp|heic|heif)$/i.test(f.type)) {
      toast.error('JPEG, PNG eller WebP tack');
      return;
    }
    try {
      const b64 = await fileToCompressedBase64(f);
      setImageB64(b64);
    } catch (err) {
      toast.error('Kunde inte läsa bilden');
    }
  };

  const onGenerate = async () => {
    if (!imageB64) { toast.error('Ladda upp en bild först 📷'); return; }
    if ((wishes || '').trim().length < 3) { toast.error('Skriv vad du vill lägga till (t.ex. "pool, pergola")'); return; }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/redesign/generate`,
        {
          image_base64: imageB64,
          space_type: spaceType,
          wishes: wishes.trim(),
          style_note: styleNote.trim(),
          variations,
        },
        { withCredentials: true, timeout: 180000 },
      );
      setResult(data);
      toast.success(`Klar! ${data.count} förslag genererade ✨`);
      await refreshHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Kunde inte generera bilder';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const openHistoryItem = async (id) => {
    try {
      const { data } = await axios.get(`${API}/redesign/history/${id}`, { withCredentials: true });
      setDetail(data);
    } catch (err) {
      toast.error('Kunde inte öppna');
    }
  };

  const deleteHistoryItem = async (id, e) => {
    e?.stopPropagation?.();
    if (!window.confirm('Ta bort denna redesign?')) return;
    try {
      await axios.delete(`${API}/redesign/history/${id}`, { withCredentials: true });
      setHistory((h) => h.filter((it) => it.id !== id));
      if (detail?.id === id) setDetail(null);
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const toggleCombine = (idx) => {
    setCombineIdxs((c) => (c.includes(idx) ? c.filter((x) => x !== idx) : (c.length < 3 ? [...c, idx] : c)));
  };

  const doCombine = async () => {
    if (!detail) return;
    if (combineIdxs.length < 2) { toast.error('Välj minst 2 varianter'); return; }
    setCombining(true);
    try {
      const { data } = await axios.post(
        `${API}/redesign/combine`,
        { entry_id: detail.id, variant_indices: combineIdxs, combine_note: '' },
        { withCredentials: true, timeout: 180000 },
      );
      toast.success('Kombinerad bild klar ✨');
      // Reload detail with the new combined result
      const { data: newDetail } = await axios.get(`${API}/redesign/history/${data.id}`, { withCredentials: true });
      setDetail(newDetail);
      setCombineIdxs([]);
      await refreshHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunde inte kombinera');
    } finally {
      setCombining(false);
    }
  };

  const activeSpace = spaceTypes.find((s) => s.id === spaceType);
  const suggestions = WISH_SUGGESTIONS[spaceType] || [];

  return (
    <div className="space-y-4" data-testid="redesign-page">
      <div className="rounded-2xl p-5 bg-gradient-to-br from-[#E8F5E9] via-[#F1F8E9] to-[#FFF3E0] border border-[#C8E6C9]">
        <div className="flex items-center gap-2 mb-1">
          <Wand2 className="w-5 h-5 text-emerald-700" />
          <h2 className="text-2xl font-semibold text-emerald-900">Redesign — visualisera ditt utrymme 🌿</h2>
        </div>
        <p className="text-sm text-emerald-900/80">
          Ladda upp ett foto av rummet, trädgården eller poolområdet. Beskriv vad du vill lägga till
          (t.ex. &quot;pool, pergola med ljusslinga, växthus&quot;) och få 2-3 AI-genererade varianter tillbaka.
        </p>
      </div>

      {/* Space type picker */}
      <div className="rounded-2xl p-4 bg-white border border-emerald-100">
        <div className="text-sm font-medium text-emerald-900 mb-2">Typ av utrymme</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {spaceTypes.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setSpaceType(s.id)}
              data-testid={`space-type-${s.id}`}
              className={`text-left rounded-xl p-3 border transition ${
                spaceType === s.id
                  ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200'
                  : 'bg-white border-slate-200 hover:border-emerald-300'
              }`}
            >
              <div className="text-2xl leading-none">{s.emoji}</div>
              <div className="text-sm font-medium text-slate-800 mt-1">{s.label}</div>
              <div className="text-[11px] text-slate-500">{s.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Upload + form */}
      <div className="rounded-2xl p-4 bg-white border border-emerald-100 grid md:grid-cols-2 gap-4">
        {/* Image upload */}
        <div>
          <div className="text-sm font-medium text-slate-800 mb-2">Bild av utrymmet</div>
          {imageB64 ? (
            <div className="relative">
              <img src={imageB64} alt="upload" className="w-full h-64 object-cover rounded-xl border border-slate-200" />
              <button
                type="button"
                onClick={() => setImageB64(null)}
                data-testid="redesign-clear-image"
                className="absolute top-2 right-2 bg-white/90 hover:bg-white text-slate-700 rounded-full p-1 border border-slate-200"
                aria-label="Ta bort bild"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="h-64 rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/30 flex flex-col items-center justify-center gap-3 text-emerald-800">
              <ImageIcon className="w-8 h-8 opacity-70" />
              <div className="text-sm">Välj eller ta ett foto av {activeSpace?.label?.toLowerCase() || 'utrymmet'}</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="redesign-upload-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                >
                  <Upload className="w-4 h-4" /> Ladda upp
                </button>
                <button
                  type="button"
                  onClick={() => camRef.current?.click()}
                  data-testid="redesign-camera-btn"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                >
                  <Camera className="w-4 h-4" /> Kamera
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
              <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickFile} />
            </div>
          )}
        </div>

        {/* Wishes / style / variations */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Vad vill du lägga till?
            </label>
            <textarea
              value={wishes}
              onChange={(e) => setWishes(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder='T.ex. "pool med romerska trappor, pergola med ljusslinga, växthus i svart aluminium"'
              data-testid="redesign-wishes"
              className="w-full rounded-xl border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setWishes((w) => (w ? `${w}, ${s}` : s))}
                    className="text-[11px] px-2 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 hover:bg-emerald-100"
                  >
                    + {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Estetik / stil (valfritt)
            </label>
            <input
              type="text"
              value={styleNote}
              onChange={(e) => setStyleNote(e.target.value)}
              maxLength={200}
              placeholder='T.ex. "modernt skandinaviskt", "medelhavsstil", "cottage-core"'
              data-testid="redesign-style"
              className="w-full rounded-xl border border-slate-200 p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">
              Antal varianter
            </label>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setVariations(n)}
                  data-testid={`redesign-variations-${n}`}
                  className={`px-4 py-1.5 rounded-full text-sm border ${
                    variations === n
                      ? 'bg-emerald-500 text-white border-emerald-500'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Fler varianter = längre väntetid (30-90 sek).</div>
          </div>

          <button
            type="button"
            onClick={onGenerate}
            disabled={busy || !imageB64 || wishes.trim().length < 3}
            data-testid="redesign-generate-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-medium hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Skapar bilder…</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Skapa {variations} förslag</>
            )}
          </button>
        </div>
      </div>

      {/* Results */}
      {result && result.images_b64?.length > 0 && (
        <div className="rounded-2xl p-4 bg-white border border-emerald-100">
          <div className="flex items-center justify-between mb-3">
            <div className="text-lg font-semibold text-emerald-900">Dina förslag ✨</div>
            <div className="flex items-center gap-3">
              {imageB64 && (
                <button
                  type="button"
                  onClick={() => setShowSlider((v) => !v)}
                  data-testid="redesign-toggle-slider"
                  className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900"
                >
                  {showSlider ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  Före/Efter-slider
                </button>
              )}
              <div className="text-xs text-slate-500">{result.count} varianter</div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {imageB64 && (
              <div className="rounded-xl overflow-hidden border border-slate-200 relative">
                <img src={imageB64} alt="original" className="w-full h-64 object-cover" />
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-slate-800/70 text-white text-[11px]">Original</div>
              </div>
            )}
            {result.images_b64.map((img, i) => {
              const src = img.startsWith('data:') ? img : `data:image/png;base64,${img}`;
              return (
                <div key={i} className="relative group">
                  {showSlider && imageB64 ? (
                    <>
                      <BeforeAfterSlider before={imageB64} after={img} label={`v${i}`} />
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-600/90 text-white text-[11px] pointer-events-none">
                        Variant {i + 1}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-xl overflow-hidden border border-emerald-200">
                      <img src={src} alt={`variant-${i}`} className="w-full h-64 object-cover" />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-emerald-600/90 text-white text-[11px]">
                        Variant {i + 1}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => downloadB64Image(img, `redesign-${spaceType}-${i + 1}.png`)}
                    className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-slate-700 rounded-full p-1.5 border border-slate-200 opacity-0 group-hover:opacity-100 transition"
                    data-testid={`redesign-download-${i}`}
                    aria-label="Ladda ner"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
              Vissa varianter kunde inte skapas: {result.errors.join('; ')}
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div className="rounded-2xl bg-white border border-emerald-100">
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          className="w-full flex items-center justify-between p-4"
          data-testid="redesign-history-toggle"
        >
          <div className="flex items-center gap-2 text-emerald-900">
            <HistoryIcon className="w-4 h-4" />
            <span className="font-medium">Historik ({history.length})</span>
          </div>
          <span className="text-slate-400 text-sm">{historyOpen ? '−' : '+'}</span>
        </button>
        {historyOpen && (
          <div className="p-4 pt-0">
            {history.length === 0 ? (
              <div className="text-sm text-slate-500 italic">Ingen historik än — skapa ditt första förslag ovan 🌿</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {history.map((it) => {
                  const st = spaceTypes.find((s) => s.id === it.space_type);
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => openHistoryItem(it.id)}
                      data-testid={`redesign-history-item-${it.id}`}
                      className="text-left rounded-xl border border-slate-200 p-3 hover:border-emerald-300 hover:bg-emerald-50/40 transition relative group"
                    >
                      <div className="text-lg leading-none">{st?.emoji || '🌿'}</div>
                      <div className="text-sm font-medium text-slate-800 mt-1">{st?.label || it.space_type}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{it.wishes}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {it.count} bild(er) · {it.created_at ? new Date(it.created_at).toLocaleDateString('sv-SE') : ''}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => deleteHistoryItem(it.id, e)}
                        className="absolute top-2 right-2 bg-white text-slate-500 hover:text-rose-600 rounded-full p-1 border border-slate-200 opacity-0 group-hover:opacity-100 transition"
                        aria-label="Ta bort"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* History detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" /> Tillbaka
              </button>
              <div className="text-sm text-slate-500">
                {detail.created_at ? new Date(detail.created_at).toLocaleString('sv-SE') : ''}
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm text-slate-700"><span className="font-medium">Önskan:</span> {detail.wishes}</div>
              {detail.style_note && (
                <div className="text-sm text-slate-700"><span className="font-medium">Stil:</span> {detail.style_note}</div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {detail.original_b64 && (
                  <div className="rounded-xl overflow-hidden border border-slate-200 relative">
                    <img
                      src={detail.original_b64.startsWith('data:') ? detail.original_b64 : `data:image/jpeg;base64,${detail.original_b64}`}
                      alt="original"
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-slate-800/70 text-white text-[11px]">Original</div>
                  </div>
                )}
                {(detail.images_b64 || []).map((img, i) => {
                  const selected = combineIdxs.includes(i);
                  return (
                    <div
                      key={i}
                      className={`rounded-xl overflow-hidden border-2 relative group ${
                        selected ? 'border-fuchsia-500 ring-2 ring-fuchsia-200' : 'border-emerald-200'
                      }`}
                    >
                      {detail.original_b64 ? (
                        <BeforeAfterSlider before={detail.original_b64} after={img} label={`d${i}`} />
                      ) : (
                        <img src={img.startsWith('data:') ? img : `data:image/png;base64,${img}`} alt={`variant-${i}`} className="w-full h-64 object-cover" />
                      )}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-emerald-600/90 text-white text-[11px] pointer-events-none">
                        Variant {i + 1}
                      </div>
                      {(detail.images_b64 || []).length >= 2 && (
                        <button
                          type="button"
                          onClick={() => toggleCombine(i)}
                          data-testid={`combine-toggle-${i}`}
                          className={`absolute top-2 left-2 rounded-full px-2 py-0.5 text-[10px] font-bold border ${
                            selected
                              ? 'bg-fuchsia-500 text-white border-fuchsia-500'
                              : 'bg-white/90 text-slate-700 border-slate-200 hover:border-fuchsia-400'
                          }`}
                        >
                          {selected ? '✓ vald' : 'kombinera'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => downloadB64Image(img, `redesign-${detail.space_type}-${i + 1}.png`)}
                        className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-slate-700 rounded-full p-1.5 border border-slate-200 opacity-0 group-hover:opacity-100 transition"
                        aria-label="Ladda ner"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
              {(detail.images_b64 || []).length >= 2 && (
                <div className="rounded-xl bg-fuchsia-50 border border-fuchsia-200 p-3">
                  <div className="text-sm font-semibold text-fuchsia-900 flex items-center gap-1.5 mb-1">
                    <Layers className="w-4 h-4" /> Kombinera favorit-delarna
                  </div>
                  <p className="text-xs text-fuchsia-800/80 mb-2">
                    Välj 2-3 varianter (klicka <span className="font-bold">kombinera</span> på varje) → AI:n bygger en ny slutbild med det bästa från alla valda.
                  </p>
                  <button
                    type="button"
                    onClick={doCombine}
                    disabled={combining || combineIdxs.length < 2}
                    data-testid="combine-generate-btn"
                    className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {combining ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Kombinerar…</>
                    ) : (
                      <><Sparkles className="w-3 h-3" /> Kombinera {combineIdxs.length || ''} varianter</>
                    )}
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => deleteHistoryItem(detail.id)}
                className="text-sm text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" /> Ta bort denna redesign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
